import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';
import { PageRequest } from '@/core/request/PageRequest';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  detailManufacturerPublic,
  getManufacturersPublic,
  getQuotationOffersPublic,
  quotationOffersPublicSend,
} from './QuotationsOffersPublic.service';
import {
  createQuotationGeneratePurchase,
  createQuotationOffersWinners,
  getQuotationListOffersSelect,
  getQuotationOffers,
  listQuotationsConfiguration,
  sendQuotationOffersConfiguration,
} from './QuotationsOffers.service';
import { ListManufacturersQuery } from '@/core/domain/operational/Manufacturers/Manufacturers.types';
import {
  QuotationOffersWinnersRequest,
  QuotationsConfigurationQuery,
  SendQuotationOffersRequest,
} from './QuotationsOffers.types';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { useIntl } from 'react-intl';

export function useQuotationOffersPublic(guid: string) {
  const query = useQuery(['quotations/offers/public', guid], () =>
    getQuotationOffersPublic(guid),
  );

  useServiceError(query.error);

  return query;
}

export function useQuotationConfiguration(
  params?: QuotationsConfigurationQuery,
) {
  const query = useInfiniteQuery(
    ['quotations', 'offers', 'configuration', { ...params }],
    ({ pageParam }) => {
      const req: QuotationsConfigurationQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listQuotationsConfiguration(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage?.result?.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useQuotationOffersConfiturationSend() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const query = useMutation(
    (obj: SendQuotationOffersRequest) => sendQuotationOffersConfiguration(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'quotations.details.offersConfiguration.save.notification.success',
          }),
          description: intl.formatMessage({
            id: 'quotations.details.offersConfiguration.save.notification.successDescription',
          }),
        });

        queryClient.invalidateQueries([
          'quotations',
          'offers',
          'configuration',
          { quotationId: data.quotationId } as QuotationsConfigurationQuery,
        ]);
      },
    },
  );

  useServiceError(query.error);

  return query;
}

export function useQuotationOffersPublicSend() {
  const queryClient = useQueryClient();
  const query = useMutation(quotationOffersPublicSend, {
    onSuccess(_, data) {
      queryClient.invalidateQueries(['quotations/offers/public', data.code]);
    },
  });

  useServiceError(query.error);

  return query;
}

export function useManufacturersDetailPublic(id?: number) {
  const query = useQuery(
    ['manufacturer', id],
    () => (id ? detailManufacturerPublic(id) : undefined),
    { enabled: !!id },
  );

  useServiceError(query.error);

  return query;
}

export function useManufacturersPublic(params: ListManufacturersQuery) {
  const query = useInfiniteQuery(
    ['manufacturers/public', { ...params }],
    ({ pageParam }) => {
      const req: ListManufacturersQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return getManufacturersPublic(req);
    },
    {
      enabled: !!params.matrixId,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useQuotationOffers(quotationId: number, params: PageRequest) {
  const query = useInfiniteQuery(
    ['quotations', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return getQuotationOffers(quotationId, req);
    },
    {
      getNextPageParam: (lastPage) => lastPage?.result?.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useQuotationListOffersSelect(
  request: boolean,
  quotationId?: number,
  params?: PageRequest,
) {
  const query = useInfiniteQuery(
    ['quotations', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return getQuotationListOffersSelect(quotationId, req);
    },
    {
      enabled: request || !!quotationId,
      getNextPageParam: (lastPage) => lastPage?.result?.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useQuotationOffersWinners() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: QuotationOffersWinnersRequest) => createQuotationOffersWinners(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: 'Sucesso!',
          description: 'Pedido de compra enviado com sucesso!',
        });

        queryClient.invalidateQueries(['quotations', data.quotationId]);
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('ultracar.errors.service_unavailable.email')
                ? 'Falha ao enviar, coloque as configurações do email.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useQuotationOffersGeneratePurchase() {
  const queryClient = useQueryClient();

  const mutation = useMutation(createQuotationGeneratePurchase, {
    onSuccess(_, data) {
      notification.success({
        message: 'Sucesso!',
        description: 'Compra foi gerada a partir da cotação com sucesso!',
      });

      queryClient.invalidateQueries(['quotations', data.quotationId]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}
